import type { QuestionMatching } from '@repo/lib/quiz/questions/class/QuestionMatching';
import type { AnswerQuestionMatching, AnswerQuestionMatchingAnswer } from '@repo/types/questions/QuestionMatching';

import { shuffleArray } from '@repo/lib/shuffleArray';
import { useEffect, useState } from 'react';

import type { QuestionMatchingProps } from '@/components/features/quiz/question/question-resolver/question-matching';

import { useQuestionState } from '.';

export function useQuestionMatching(questionMatchingProps: QuestionMatchingProps) {
  const { question, sectionIndex, questionIndex } = questionMatchingProps;
  const { getContent, getMarked, getAnswer, setAnswer, resetAnswer, exportedAnswer } = useQuestionState<QuestionMatching>(question, sectionIndex, questionIndex);

  const [selectedLeft, setSelectedLeft] = useState<string>('');
  const [selectedRight, setSelectedRight] = useState<string>('');

  const questionContent = getContent();
  const selectedAnswer = getAnswer() || [];
  const exportedUserAnswer = exportedAnswer();

  const marked = getMarked();

  const getByColumns = (answers: AnswerQuestionMatching[]) => {
    const left: AnswerQuestionMatchingAnswer[] = [];
    const right: AnswerQuestionMatchingAnswer[] = [];

    answers.forEach((pair) => {
      left.push(pair.left);
      right.push(pair.right);
    });

    return [shuffleArray(left), shuffleArray(right)];
  };

  const [columns] = useState<AnswerQuestionMatchingAnswer[][]>(getByColumns(questionContent.answers));

  const getUserPairsFromValue = () => {
    const pairs: AnswerQuestionMatching[] = [];

    selectedAnswer.forEach(({ left, right }) => {
      const leftAnswer = columns[0]?.find((answer) => answer.id === left);
      const rightAnswer = columns[1]?.find((answer) => answer.id === right);

      if (leftAnswer && rightAnswer) {
        pairs.push({ left: leftAnswer, right: rightAnswer, points: 0 });
      }
    });

    return pairs;
  };

  const userPairs = getUserPairsFromValue();

  const isAnswerUsed = (answerId: string, column: 'left' | 'right'): boolean => {
    if (column === 'left') {
      return selectedAnswer.some((pair) => pair.left === answerId);
    } else {
      return selectedAnswer.some((pair) => pair.right === answerId);
    }
  };
  const handleAnswerClick = (answerId: string, column: 'left' | 'right') => {
    if (isAnswerUsed(answerId, column)) return;

    if (column === 'left') {
      setSelectedLeft(answerId);
    } else {
      setSelectedRight(answerId);
    }
  };

  const getPairNumber = (answerId: string, column: 'left' | 'right'): number | null => {
    const pairIndex = userPairs.findIndex((pair) => (column === 'left' && pair.left.id === answerId) || (column === 'right' && pair.right.id === answerId));
    return pairIndex !== -1 ? pairIndex + 1 : null;
  };

  const clearSelections = () => {
    setSelectedLeft('');
    setSelectedRight('');
  };

  const clearAllPairs = () => {
    resetAnswer();
    clearSelections();
  };

  const getMarkedState = (answer: AnswerQuestionMatchingAnswer) => {
    const markedAnswerLeft = marked?.answers?.find((pair) => pair.match?.left === answer.id);
    const markedAnswerRight = marked?.answers?.find((pair) => pair.match?.right === answer.id);
    if (markedAnswerLeft) {
      return markedAnswerLeft.correct;
    }
    if (markedAnswerRight) {
      return markedAnswerRight.correct;
    }
    return undefined;
  };

  useEffect(() => {
    if (selectedLeft && selectedRight) {
      const newValue = [
        ...(getAnswer() || []),
        {
          left: selectedLeft,
          right: selectedRight,
        },
      ];

      setAnswer(newValue);
      clearSelections();
    }
  }, [selectedLeft, selectedRight, getAnswer, setAnswer]);

  return {
    marked,
    selectedLeft,
    selectedRight,
    answers: userPairs,
    columns,
    isAnswerUsed,
    handleAnswerClick,
    getPairNumber,
    clearAllPairs,
    getMarkedState,
    exportedUserAnswer,
  };
}
